import type { UnifiedReviewItem } from '@/types/review';

/**
 * A review that carries no star rating at all.
 *
 * Every sync client coerces a missing rating to `0` — Google maps
 * `STAR_RATING_UNSPECIFIED` to it, Yelp and Facebook both write `r.rating ?? 0`
 * — and a Facebook recommendation genuinely has no stars to give. So `0` on an
 * external review means "unrated", never "nought out of five".
 *
 * One predicate rather than an inline `rating > 0` at each site, because this
 * rule was already known in three places on the server (the stats aggregate,
 * the 30-day trend, the response list filter) and unknown in the two places
 * the customer actually looks. That split is what produced the bug: the strip
 * started reporting a correct 5.00 while the rows beneath it read `5` and `0`,
 * which makes the aggregate look broken rather than the row.
 *
 * Rating-0 review RESPONSES cannot reach here — those are click-through
 * placeholders and the repository filters them out of the list entirely. A
 * zero in this model is an unrated external review.
 *
 * Raised in review on #2434.
 */
export function isUnratedReview(item: Pick<UnifiedReviewItem, 'rating'>): boolean {
  return !(item.rating > 0);
}

/** What an unrated review shows where a score would go. */
export const UNRATED_LABEL = '—';
